/**
 * The pre-reload teardown, and the sign-out wipe inside it.
 *
 * `MyApp.reloadAppAsync` is what the app calls instead of `location.reload()`. Before the page
 * actually reloads it has work to do: flush telemetry, and — when the reload is a SIGN-OUT — wipe
 * everything the person signing out left on this device. That wipe is what stops the previous
 * user's settings, authentication and data reaching whoever signs in next.
 *
 * **Ordering is the whole design here, and the order below is deliberate at every step.** The
 * synchronous, instant clears (localStorage, sessionStorage, cookies) run BEFORE the slow,
 * bounded, can-hang database deletions, because they are what actually keeps the session off the
 * device; if a deletion ate the reload budget while they still waited their turn, the app would
 * reload still carrying the previous user's session. They then run AGAIN at the end, because live
 * sync keeps writing for the seconds the deletions take. Four timeouts nest inside one another so
 * that one wedged step costs us that step and nothing else. And `beginWipeUntilReload()` is
 * declared before anything is destroyed, so the database-recovery machinery does not answer our
 * own wipe as if it were corruption. Each rule has its reason written beside it below.
 *
 * **It lives in its own file, free of Angular, so those rules can be tested by RUNNING them.**
 * This is the same split `signout-teardown.ts`, `legacy-idb-cleanup.ts` and
 * `database-wrapper-sync-start.ts` already use, for the same reason: `app.component.ts` sits on a
 * component with a great many injected services and cannot be driven from a unit test. While this
 * code was on that component, every rule above was defended instead by tests that read
 * `app.component.ts` as a STRING and compared substring offsets — which could not tell a real
 * regression from a rename, and could equally stay green while the behaviour broke. Those are
 * gone; `reload-teardown.test.ts` drives this module with fake storage, a fake IndexedDB, a fake
 * cache store and a fake reload, and asserts what the teardown DOES. Deferred finding
 * `df-c4c264e58e9e` recorded that trade; internal ticket #280 carries the discussion.
 *
 * Nothing here reaches for a browser global. Every collaborator arrives in `ReloadTeardownDeps`,
 * which is what makes the fakes possible — and, incidentally, what lets the two preserved storage
 * keys be imported from the modules that define them rather than retyped as literals, which they
 * had to be while a test was matching them by name in this file's source text.
 *
 * See `design-docs/boot-sequence.md` ("L8: a pending reload suppresses the watchdog", and the
 * bounds table) and `design-docs/local-storage-and-auto-plan-cache.md` for the incidents behind
 * these rules.
 */

import { timeout, TimeoutError } from '@cf-shared/shared-util';
import { errorReportFields } from '../services/error-patterns';
import {
  deleteIdbDatabasesAsync,
  IdbDeletionOutcome,
  IdbDeletionResult,
  IdbForDeletion,
} from './idb-database-deletion';
import { SQLITE_MIGRATION_DONE_KEY } from './legacy-idb-cleanup';
import { HANDLED_LAUNCH_URL_KEY } from '../services/deep-link-signin';
import {
  LOCAL_DATABASE_ENGINE_KEY,
  SQLITE_COACH_COPY_NAMES_KEY,
  SQLITE_MIGRATION_ROLLED_BACK_KEY,
  SQLITE_MIGRATION_TARGETS_KEY,
  SQLITE_READY_BOOTS_KEY,
  SQLITE_ROLLBACK_COMPLETION_KEY,
} from '../services/local-database-engine-decisions';
import { APP_INSTALL_STARTUP_OPT_OUT_KEY } from '../services/app-install-resolver';
import { DEVICE_LEVEL_LOCALSTORAGE_KEYS, DEVICE_LEVEL_LOCALSTORAGE_PREFIXES } from '../services/local-storage-hygiene';
import type { ReloadAppOptions } from './app-interface';
import { exerciseDatabaseName } from '@cf-shared/data-model/exercise';

// how long the reload waits for its pre-reload teardown before reloading anyway. The teardown
// can hang, not just throw (the telemetry flush retries a failing server-side flush in an
// unbounded backoff loop), and a reload that never fires strands the app for good — see the
// comment in runReloadTeardownAsync. Generous enough that a healthy teardown always finishes
// first: the flush and the IndexedDB/SQLite deletions are normally sub-second, and blocked
// deletions resolve rather than hang. This is a backstop, not a budget.
export const RELOAD_PREPARE_TIMEOUT_MS = 10_000;

// Three bounds NESTED inside RELOAD_PREPARE_TIMEOUT_MS, one per step of the teardown that can hang
// indefinitely. Each is deliberately well under it, because the outer bound is a last resort: when it
// fires, the app reloads with the wipe unfinished. Bounding each hazard separately means one wedged
// step costs us that step and nothing else, so the rest of the wipe still runs. Each reports its own
// silent problem, so a hang is never mistaken for the misleading ReloadPrepareFailed. A healthy
// teardown clears all three in milliseconds; these are backstops, not budgets. See their call sites for
// why each step hangs.
//
// They sum to 9 s of the 10 s outer budget, which sounds tight but is not the thing that protects the
// wipe. What protects it is ORDER: the session-critical clears (localStorage, sessionStorage, cookies)
// are synchronous and run BEFORE all of this, so even the worst case — every bound firing — cannot
// reload the app still carrying the previous user's session.
export const RELOAD_TELEMETRY_FLUSH_TIMEOUT_MS = 3_000;
export const FIREBASE_SHUTDOWN_TIMEOUT_MS = 2_000;
// the image cache folder delete (#5692). a few hundred small files; far quicker than this
export const NATIVE_IMAGE_CACHE_DELETE_TIMEOUT_MS = 2_000;
export const SIGNOUT_DB_DELETION_TIMEOUT_MS = 4_000;

/** sessionStorage key marking this browser session as an E2E test run. */
const E2E_TEST_FLAG_KEY = '__CF_E2E_TEST__';

/** The part of LoggingService this teardown needs, named so the teardown stays testable. */
export interface TeardownLogging {
  trackSilentProblem(name: string, context: { [key: string]: any }): void;
}

/** The part of DatabaseRecoveryService this teardown needs, named for the same reason. */
export interface TeardownDatabaseRecovery {
  /**
   * Tell the database-recovery machinery that everything failing from here on is our own doing.
   * Never turned back off — the page reloads instead. See
   * `DatabaseRecoveryService.beginWipeUntilReload`.
   */
  beginWipeUntilReload(): void;
}

/** The `localStorage` / `sessionStorage` surface this teardown uses. */
export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/** The `caches` surface this teardown uses. */
export interface CacheStorageLike {
  keys(): Promise<string[]>;
  delete(key: string): Promise<boolean>;
}

/** Deletes one SQLite database by name, on the platforms that have any. */
export type SqliteDatabaseDeleter = (sqliteName: string) => Promise<unknown>;

/** Everything the teardown reaches outside itself. */
export interface ReloadTeardownDeps {
  logging: TeardownLogging;
  databaseRecovery: TeardownDatabaseRecovery;
  /** `TelemetryService.flushAsync` — joins the shared background flusher, so it can hang. */
  flushTelemetryAsync(): Promise<unknown>;
  /** `FirebaseService.shutdownAsync` — closes Firebase's own local databases. */
  shutdownFirebaseAsync(): Promise<unknown>;
  /**
   * The local database names to delete on the SQLite path. Read LAZILY, at the moment that block
   * runs, and it must be the COMPLETE list rather than the active one (#4506) — by then sign-out
   * has shut every database down, so a filtered list would be empty and the previous user's files
   * would stay on the device. See design-docs/pass-through-db.md, "Which databases a sweep may
   * act on".
   */
  localDatabaseNames(): (string | null | undefined)[];
  localStorage: StorageLike;
  sessionStorage: StorageLike;
  indexedDb: IdbForDeletion;
  caches: CacheStorageLike;
  /** `clearAllCookies` from services/browser-utils. */
  clearCookies(): void;
  /** `AuthService.clearWarmResumeFlag` — forces the next boot to be a cold start. */
  clearWarmResumeFlag(): void;
  /** Rewrite the address bar to `/` WITHOUT triggering navigation (`history.replaceState`). */
  replaceUrlWithRoot(): void;
  /**
   * Loads the SQLite delete function, or `null` on a platform with no SQLite databases (anything
   * that is not iOS native). Null means the whole SQLite block is skipped.
   *
   * It is a loader rather than the deleter itself because the real one arrives through a dynamic
   * `import()`, and an import that FAILS must be reported as a cleanup failure covering the whole
   * block — not as one database's delete failing.
   */
  loadSqliteDeleterAsync: (() => Promise<SqliteDatabaseDeleter>) | null;
  /**
   * Deletes the native apps' offline image cache folder (#5692): the previous user's photos. It
   * answers at once on the web, where there is no such folder. Optional so a test that is not about
   * it need not supply one.
   */
  deleteNativeImageCacheAsync?: () => Promise<void>;
}

// Is this IndexedDB database owned by the SERVICE WORKER rather than by the page?
//
// Workbox keeps its own `workbox-*` databases (cache-expiration bookkeeping, see client/templates/sw.js)
// open for as long as the service worker is alive. That is a different connection from the page, and
// one the page cannot close. A deleteDatabase() issued against such a database therefore answers
// NOTHING — not success, not error, not even blocked — for as long as the worker holds it. Awaiting one
// is awaiting a promise that cannot settle, so the wait can only ever end by hitting its own timeout.
//
// That is the normal and only possible answer, and it happens on EVERY sign-out. So the wipe neither
// WAITS on these (blocking on them cost every sign-out the full SIGNOUT_DB_DELETION_TIMEOUT_MS) nor
// ALARMS on them (a report that fires every time buries the genuinely wedged database it exists to
// catch). The delete is still ISSUED for them — see the deletion loop for what that does and does not
// buy us.
//
// If you add a SECOND service worker, its databases will not match this prefix (a Firebase-messaging
// worker, for instance, opens `firebase-messaging-database`) and the stall comes straight back. Widen
// this predicate then.
//
// The name is optional in IDBDatabaseInfo, so this must tolerate a nameless entry rather than throw.
// It is called from outside the enumeration's try/catch, where a throw would skip the rest of the
// teardown.
export function isServiceWorkerOwnedDb(dbName: string | undefined): boolean {
  return dbName?.startsWith('workbox-') ?? false;
}

/** Which of the two storages a preserved key lives in — the property name on `ReloadTeardownDeps`. */
type PreservedKeyStorage = 'localStorage' | 'sessionStorage';

// Every key that survives the sign-out wipe, and WHY each one has to.
//
// This is a list rather than a run of hand-written statements because the snapshot and the restore
// used to be written separately, about twenty-five lines apart, once per key. Writing the snapshot
// half and forgetting the restore half fails SILENTLY — an absent key looks exactly like a key that
// was never there — and forgetting this function was half of what left a signed-out account's whole
// database on a phone in #5453. With one list there is no second half to forget.
//
// The versioned keys are IMPORTED from the modules that define them. They used to be retyped as
// literals here, because a source tripwire read this code as text and matched each key by name — so
// renaming one could leave the wipe preserving a key nothing reads any more. The tests run the code
// now, so the constants can be used directly.
const KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE: readonly { storage: PreservedKeyStorage; key: string }[] = [
  // so a test run stays recognisable as one across the deletion.
  { storage: 'sessionStorage', key: E2E_TEST_FLAG_KEY },

  // the iOS legacy-IndexedDB cleanup marker. Carrying it across is NOT the same as keeping it
  // forever: whoever deletes the SQLite databases removes it once they are really gone, and this
  // wipe simply never destroys that decision — so the wipe must not be the thing that drops it.
  { storage: 'localStorage', key: SQLITE_MIGRATION_DONE_KEY },

  // the mark naming the cold-start launch link this web-view session has already acted on. It is
  // neither the previous user's data nor their authentication — it is bookkeeping about a link the
  // operating system is STILL holding, and which it keeps handing back for as long as this window
  // lives. Destroying it made signing out sign the user straight back in: the boot after the wipe
  // found no mark, and the other guard ("the web view is already showing that address") could not
  // match either, because consuming the token had already stripped it out of the address bar. So
  // boot acted on the launch link again and reloaded to the sign-in address. Signing in again from
  // the same email link is unaffected — a link tapped while the app runs goes through the
  // deliberately unguarded `appUrlOpen` listener. See design-docs/purchase-deep-linking.md, "The
  // launch link is acted on ONCE per web-view session".
  { storage: 'sessionStorage', key: HANDLED_LAUNCH_URL_KEY },

  // WHICH ENGINE this device's local data is in (#4981 phase 2). It is also on
  // DEVICE_LEVEL_LOCALSTORAGE_KEYS, which this wipe now keeps too, but it stays named here
  // with its reason: drop it and a teardown that times out mid-way reloads the
  // device with the marker gone and its SQLite data still on disk. See
  // design-docs/android-local-database-engine.md, "Some notes must survive BOTH
  // localStorage wipes".
  { storage: 'localStorage', key: LOCAL_DATABASE_ENGINE_KEY },

  // WHICH SQLITE FILES a background copy has written into (#4981 phase 3, #5449). The same
  // argument as the engine marker above, one step earlier in the migration: a device still
  // running on IndexedDB can be carrying SQLite files full of the signed-in user's data,
  // and this note is the only thing that can name them. It is read LATE — the SQLite block
  // in prepareForReloadAsync asks for the names long after this wipe — so dropping it here
  // leaves the deleter with nothing to delete. That is half of what made #5453's check 3
  // fail on a real phone.
  //
  // Like the cleanup marker, carrying it across is NOT the same as keeping it for ever: the
  // SQLite block removes it once those files are really gone, and this wipe must not be
  // the thing that destroys that decision. See
  // design-docs/android-local-database-engine.md, "Some notes must survive BOTH
  // localStorage wipes".
  { storage: 'localStorage', key: SQLITE_MIGRATION_TARGETS_KEY },

  // WHICH COACH COPIES of client databases manage mode opened on this device
  // (df-a00755b05133). The same argument as the targets note above: "Back to my account"
  // closes the copy, so this note is the only thing that can name its SQLite file, and it
  // is read LATE, by the SQLite block. The block removes it once the files are gone. See
  // design-docs/coach-mode.md, "The copy is kept when the coach leaves manage mode".
  { storage: 'localStorage', key: SQLITE_COACH_COPY_NAMES_KEY },

  // THAT PHASE 4 ROLLED THIS DEVICE BACK, and WHICH SQLITE FILES it left behind (#4981
  // phase 4, #5270). The same argument as the two entries above, pointed the other way: the
  // engine marker now says `idb`, so the sign-out deleter is disarmed, and this note is the
  // only thing that can still name the superseded SQLite files. It also carries the re-arm
  // generation that stops the device migrating straight back onto the engine it was just
  // taken off.
  { storage: 'localStorage', key: SQLITE_MIGRATION_ROLLED_BACK_KEY },

  // THE ROLLBACK'S COMMIT POINT (#4981 phase 4, #5270). Read at the TOP of a later page
  // load, which is about as late as a value can be read, so this wipe must not be what
  // destroys it. Losing it mid-tail leaves the device reading one engine while a verified
  // copy of its data sits at a generation nothing names — the orphan the record exists to
  // prevent. The tail's last step clears it once the old databases are really gone.
  { storage: 'localStorage', key: SQLITE_ROLLBACK_COMPLETION_KEY },

  // HOW MANY SUCCESSFUL BOOTS this device has run on SQLite (#4981 phase 4, #5270). This
  // wipe is the one that makes the entry load-bearing: it runs on the
  // fatal-error "start this device over" button and on a teardown bounded at 10 seconds
  // that may die part-way. Neither of those is a completed sign-out, so neither should cost
  // the device a grace period it has already served. A completed sign-out zeroes the
  // counter deliberately, elsewhere.
  { storage: 'localStorage', key: SQLITE_READY_BOOTS_KEY },

  // the startup install sheet's "Don't Show Again" choice (#5707). it belongs to the
  // browser, not the account, and the login page runs this wipe on every web sign-out,
  // so without this entry signing out brings the sheet back.
  { storage: 'localStorage', key: APP_INSTALL_STARTUP_OPT_OUT_KEY },
];

// The named keys above, plus every key the FIRST sign-out wipe keeps
// (DEVICE_LEVEL_LOCALSTORAGE_KEYS in local-storage-hygiene.ts, where each one's reason is written).
// The login page runs this wipe straight after that one on every sign-out, so a device-level key it
// did not keep was lost anyway: the UI language, the local telemetry opt-in and the rest
// (df-d492047e8ce8). Of that wipe's prefixes, only our own device-level ones
// (DEVICE_LEVEL_LOCALSTORAGE_PREFIXES, e.g. the update prompt's dismissal stamps, df-c49e86ccdb9a)
// are kept here, by clearSessionStorageAndCookies below. The SDK prefixes that wipe also keeps
// (`firebase`, `mp_`, ...) must still go here — a late Mixpanel write re-creates `mp_*` with the
// previous user's id, which is part of why this wipe runs at all.
const ALL_KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE: readonly { storage: PreservedKeyStorage; key: string }[] = [
  ...KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE,
  ...DEVICE_LEVEL_LOCALSTORAGE_KEYS
    .filter(key => !KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE.some(e => e.storage === 'localStorage' && e.key === key))
    .map(key => ({ storage: 'localStorage' as const, key })),
];

// The synchronous half of the sign-out wipe: localStorage, sessionStorage and cookies — the state that
// actually stops the previous user's settings and authentication reaching the next one. Called twice
// (see both call sites), so it lives in one place rather than being duplicated with a subtle drift.
//
// It clears EVERYTHING and then puts back the keys in ALL_KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE
// above, plus every localStorage key starting with a DEVICE_LEVEL_LOCALSTORAGE_PREFIXES entry.
// A device-level key needs nothing here beyond its entry in DEVICE_LEVEL_LOCALSTORAGE_KEYS;
// a key only this wipe must keep (a sessionStorage one, say) is one entry in the named list.
export function clearSessionStorageAndCookies(deps: ReloadTeardownDeps) {
  // for each preserved key, take its value aside before the clear wipes it
  const preserved = ALL_KEYS_PRESERVED_ACROSS_THE_SIGNOUT_WIPE.map(
    entry => ({ ...entry, value: deps[entry.storage].getItem(entry.key) }),
  );
  // for each localStorage key under a device-level prefix, take it aside too. collect the names
  // first: the storage must not be read by index while anything changes it.
  const prefixedKeys: string[] = [];
  for (let i = 0; i < deps.localStorage.length; i++) {
    const key = deps.localStorage.key(i);
    if (key != null && DEVICE_LEVEL_LOCALSTORAGE_PREFIXES.some(prefix => key.startsWith(prefix))) {
      prefixedKeys.push(key);
    }
  }
  for (const key of prefixedKeys) {
    preserved.push({ storage: 'localStorage', key, value: deps.localStorage.getItem(key) });
  }

  deps.localStorage.clear();
  deps.sessionStorage.clear();

  // for each preserved key, put it back — but only if it was set, so the wipe never invents one
  for (const entry of preserved) {
    if (entry.value != null) {
      deps[entry.storage].setItem(entry.key, entry.value);
    }
  }
  deps.clearCookies();
}

// everything the reload does BEFORE the reload itself. Split out so the caller can bound it
// and guarantee the reload in a finally — see the comment there for why that matters.
export async function prepareForReloadAsync(deps: ReloadTeardownDeps, opts: ReloadAppOptions) {
  // if we must come back cold, do it FIRST — before any await. The caller bounds this whole
  // function, so anything after a hang is skipped, and the very next await (the telemetry flush)
  // is the one that can hang. Leaving this to the end meant a hung flush reloaded the app with
  // the warm-resume flag still set and the URL not reset — so it would warm-resume straight back
  // into the stale local state resetToRoot exists to prevent, and come back on the same deep-link
  // URL (e.g. a coach-claim token). This block is synchronous and order-independent (in the
  // clearLocalData path localStorage.clear() below wipes these keys anyway), so hoisting it is
  // free and makes a timeout truncate only the wipe — the trade-off we actually accept.
  if (opts.resetToRoot) {
    // force cold start — resetToRoot is used after state changes (purchase, coach claim,
    // manage client) where local PouchDB may be stale
    deps.clearWarmResumeFlag();
    deps.localStorage.removeItem('warm_resume_url');
    deps.localStorage.removeItem('warm_resume_url_ts');
    // Use replaceState to change URL without triggering navigation events
    // This ensures the URL is updated synchronously before reload
    deps.replaceUrlWithRoot();
  }

  // flush pending telemetry deletes before databases close, to avoid stale events in IDB.
  //
  // BOUNDED, and it must be, because this flush is the FIRST await of the teardown and everything
  // that wipes the device runs after it. flushAsync joins the shared background flusher, whose
  // server-side retry is an unbounded backoff loop (up to 300 s per sleep, forever) — a single event
  // Mixpanel permanently refuses is enough to park it there. Left unbounded, that flush alone eats
  // the whole RELOAD_PREPARE_TIMEOUT_MS budget, so the app reloads with NOTHING wiped: the previous
  // user's databases, localStorage, cookies and auth all still on the device, reported only as a
  // misleading ReloadPrepareFailed. Bounding the flush separately means a wedged flusher costs us
  // the flush and nothing else — the wipe always runs. Giving up here is cheap: on the clearLocalData
  // path the database holding those events is about to be deleted anyway.
  try {
    await timeout(RELOAD_TELEMETRY_FLUSH_TIMEOUT_MS, deps.flushTelemetryAsync());
  } catch (err) {
    deps.logging.trackSilentProblem('ReloadTelemetryFlushGaveUp', {
      ...errorReportFields(err),
      // same two-condition split as PurgeTelemetryFlushGaveUp: a timeout means the flusher is wedged
      // (the backoff loop), a throw means the flush itself failed. They need different follow-ups.
      gave_up_reason: err instanceof TimeoutError ? 'timeout' : 'flush_threw',
      clear_local_data: opts.clearLocalData ?? false,
    });
  }

  if (opts.clearLocalData) {

    // everything below this line destroys the local databases on purpose, so from here on an
    // IndexedDB error is our own doing and must not be answered as if it were corruption. Say so
    // FIRST — before the firebase shutdown, and well before the deletions — because the moment we
    // start tearing databases down, recovering one is pointless: the page is going to reload and the
    // stores are going away. Without this marker the recovery machinery treated our own wipe as
    // corruption and recreated the PouchDB instances (re-creating the very stores we had just
    // deleted) and restarted sync on databases sign-out had already shut down. See
    // DatabaseRecoveryService.beginWipeUntilReload for the full mechanism and why it never gets turned back off.
    deps.databaseRecovery.beginWipeUntilReload();

    // NOTE: cancelling live sync here (`dbProvider.allDatabases` → `cancelSync()`) once looked like the
    // fix for the never-firing IndexedDB delete, on the theory that live sync was the connection
    // re-opening the database under the delete. It was tried and REVERTED, because it destabilises the
    // NEXT boot: the sign-out E2E scenario then failed 2 of 3 local runs with a NavRestoreStuck (a nav
    // restore wedged for 20 s) and a click intercepted by a page transition that never settled, where
    // the same scenario passes cleanly without it. Why is still not understood.
    //
    // The theory behind it is now DISPROVEN anyway, so do not revive it hoping it fixes the delete. The
    // enumeration log below finally named the silent database, and it is not a Pouch database at all:
    // every `_pouch_*` database settles normally, and the only one that answers nothing is the SERVICE
    // WORKER's `workbox-expiration` (see isServiceWorkerOwnedDb). Live sync was never the re-opener.
    // Do not re-add the cancel without explaining that boot regression first.

    // ensure that the firebase is not keeping its local databases open. BOUNDED: this sits between
    // the two bounded steps on the wipe path, so an unbounded hang here would still let the outer
    // reload bound fire before localStorage / cookies / caches are cleared — the very leak the nested
    // bounds exist to prevent. Firebase's own IndexedDB deletions report `blocked` even after this
    // resolves, so it does not fully close them anyway; we do not depend on it having succeeded.
    try {
      await timeout(FIREBASE_SHUTDOWN_TIMEOUT_MS, deps.shutdownFirebaseAsync());
    } catch (err) {
      deps.logging.trackSilentProblem('SignOutFirebaseShutdownGaveUp', errorReportFields(err));
    }

    // clear localStorage, sessionStorage and cookies EARLY — before the database deletions, not after.
    // These are what actually stop the previous user's settings and authentication reaching the next
    // one, and they are synchronous and instant. The deletions below are the slow, bounded, can-hang
    // part; if they ate the whole reload budget while these still waited their turn at the END of the
    // teardown, the app would reload still carrying the previous user's session — the precise leak
    // this block exists to prevent. Cheap and critical goes first; slow and best-effort goes last.
    //
    // It runs AGAIN at the end, and that is not belt-and-braces paranoia — it closes a real hole this
    // early clear opens. The app keeps running for seconds after this point (the bounded deletions,
    // the SQLite deletes, the cache purge), and live PouchDB sync is still running with it. Its
    // `paused` handler re-creates `db_backgroundSyncComplete_*` in localStorage whenever that key is
    // absent — which it now is, because we just cleared it. That key surviving the reload would make
    // the next sign-in believe all history is already local (for a database we just deleted) and skip
    // the on-demand range pulls. Mixpanel persists to localStorage too, so any event fired in this
    // window re-writes `mp_*` with the previous user's distinct_id. Clearing at BOTH ends means a hang
    // cannot leave the session behind and a late writer cannot resurrect it.
    clearSessionStorageAndCookies(deps);

    /* clear local cache of databases when logging out
    this serves two purposes:
    1. we don't leave stale user data on the device
    2. on next login, the app can't finish booting before it loads the databases,
    which ensures it doesn't boot without finishing couch login
    */

    // A deletion has MORE THAN TWO possible outcomes, and each needs its own answer here:
    //   deleted      — the database is gone.
    //   failed       — the delete was refused; that database survives, so name it
    //                  (SignOutDbDeletionFailed).
    //   blocked, or an upgrade asked for — another connection has it open. Treated as settled and
    //                  reported nowhere; see the paragraph below.
    //   no answer    — the request fires NO event at all: not success, not error, not blocked.
    //
    // That last outcome is why the shared helper's wait is BOUNDED. A delete issued against a
    // database that another connection holds open sits silently in the IndexedDB connection queue and
    // fires nothing. An unbounded wait then parks the whole teardown until the 10 s
    // RELOAD_PREPARE_TIMEOUT_MS fires, which reloads the app with localStorage, cookies and the caches
    // still untouched — and those, not the databases, are what keep the previous user's session off
    // the device. Waiting forever for a perfect wipe therefore produces a WORSE wipe than giving up.
    // So: wait, but not forever, and carry on with the rest.
    //
    // The one connection we KNOW does this is the service worker's, and its databases are excluded from
    // the wait entirely (see isServiceWorkerOwnedDb) rather than left to burn the bound on every
    // sign-out. Live sync was once blamed for it; that is disproven — every `_pouch_*` database settles
    // normally. The bound now guards against a re-opener we have not met yet.
    //
    // A BLOCKED deletion is settled as far as sign-out is concerned, and nothing is reported about it.
    // The legacy-IndexedDB cleanup reads that same outcome the OTHER way; `idb-database-deletion.ts`
    // says why the two callers are allowed to disagree.
    //
    // The deletions themselves, and the bounded wait, are shared with that cleanup and live in
    // `idb-database-deletion.ts` (df-c5943ac55882). What stays here is everything that is sign-out's
    // own: which databases are enumerated, which of them the wait may block on, this bound, and the
    // two reports below.
    let deletions: IdbDeletionResult[] = [];
    try {
      // for each IDB database
      const dbs = await deps.indexedDb.databases();
      // log what we are about to delete, not just the outcomes. A delete that fires no event leaves no
      // log line at all, and you cannot grep for an absence — so without this, a silent database is
      // invisible. With it, it is a simple subtraction against the `Deleted database …` lines the
      // shared helper writes. design-docs/boot-sequence.md states that mechanism as fact.
      console.log(`Deleting IDB databases (${dbs.length}): ${dbs.map(db => db.name).join(',')}`);
      // the name is optional in IDBDatabaseInfo and a nameless entry is passed through exactly as it
      // always was, rather than being skipped — changing that would change which databases get a
      // delete issued at all.
      deletions = await deleteIdbDatabasesAsync(
        deps.indexedDb,
        dbs.map(db => db.name as string),
        {
          timeoutMs: SIGNOUT_DB_DELETION_TIMEOUT_MS,
          shouldWaitFor: (name) => !isServiceWorkerOwnedDb(name),
        },
      );
    } catch (e) {
      // trackSilentProblem, NOT trackAbnormalException — same reason as the reload-prepare catch:
      // trackAbnormalException routes through interceptDatabaseError first, and THIS is the most
      // database-flavoured failure in the teardown, so it is the one most likely to be intercepted
      // and to re-enter the reload in the middle of its own teardown.
      deps.logging.trackSilentProblem('SignOutDbEnumerationFailed', errorReportFields(e));
    }

    // What the un-awaited service-worker delete actually buys us, stated honestly, because this is the
    // safety claim the exclusion rests on: the request is issued, and the browser is free to complete it
    // once the worker's connection closes — but this page reloads moments later, so we do NOT get to
    // observe that, and it is not guaranteed. Sign-out does not depend on it. What protects the user is
    // the rest of this teardown, which does not need the database gone: every Cache Storage cache is
    // deleted below, so the rows in `workbox-expiration` are bookkeeping pointing at caches that no
    // longer exist, and it holds no credentials or session state (localStorage, sessionStorage and
    // cookies are cleared separately, and BEFORE the slow deletions). If a stale row does survive, the
    // next user's worker overwrites it as it re-populates its caches.
    //
    // One failed deletion must not abort the sign-out cleanup that follows (SQLite deletes,
    // localStorage, sessionStorage, cookies, caches). That is why the helper reports a refusal as a
    // result instead of throwing: throwing would skip all of it, and since the reload always happens
    // the app would come back up on the login page still carrying the previous user's settings and
    // auth — the exact leak this block exists to prevent.
    const awaitedDeletions = deletions.filter(deletion => !isServiceWorkerOwnedDb(deletion.name));

    // WHICH databases never answered is the whole point of this report — the one fact that identifies
    // the wedging database, recoverable from no other log. So report only when we can actually name
    // one: a stall report naming nobody carries no information at all.
    const stalledDbNames = awaitedDeletions
      .filter(deletion => deletion.outcome === IdbDeletionOutcome.NoAnswer)
      .map(deletion => deletion.name);

    if (stalledDbNames.length > 0) {
      deps.logging.trackSilentProblem('SignOutDbDeletionStalled', {
        timeout_ms: SIGNOUT_DB_DELETION_TIMEOUT_MS,
        // awaited_count, not total_count: SignOutDbDeletionFailed below reports total_count over ALL
        // enumerated databases, and these two numbers are read side by side in Sentry. Naming them the
        // same while counting different populations is how one of them gets silently mis-read.
        awaited_count: awaitedDeletions.length,
        stalled_db_names: stalledDbNames.join(','),
      });
    }

    // report genuine failures in BOTH cases. A database can be refused while a DIFFERENT one stalls,
    // and that refusal is a survivor — the leak's blast radius — so it must reach Sentry either way.
    // Reading every database's own outcome rather than one combined answer is what makes that possible.
    const failures = deletions.filter(deletion => deletion.outcome === IdbDeletionOutcome.Failed);
    if (failures.length > 0) {
      deps.logging.trackSilentProblem('SignOutDbDeletionFailed', {
        failed_count: failures.length,
        total_count: deletions.length,
        // name the databases that survived, so the leak's blast radius is visible in Sentry
        failed_db_names: failures.map(failure => failure.name).join(','),
        // a refusal always has to carry SOMETHING, or the report says a deletion failed and gives no
        // reason at all. The browser fills the request's error in; the fallback is for a request that
        // reports a failure without one.
        ...errorReportFields(failures[0].error ?? new Error('unknown deletion error')),
      });
    }

    // on iOS native, also delete SQLite databases used by the capacitor-sqlite adapter. A null
    // loader means this platform has none.
    if (deps.loadSqliteDeleterAsync) {
      try {
        const deleteSqliteDatabaseAsync = await deps.loadSqliteDeleterAsync();
        const failedSqliteNames: string[] = [];
        let firstSqliteError: unknown;
        // the COMPLETE list, deliberately, NOT `activeDatabases` (#4506): by the time
        // this runs sign-out has shut every database down, so a filtered list would be
        // empty and the previous user's files would stay on the device. See
        // design-docs/pass-through-db.md, "Which databases a sweep may act on".
        for (const localName of deps.localDatabaseNames()) {
          if (localName) {
            const sqliteName = '_pouch_' + localName;
            try {
              await deleteSqliteDatabaseAsync(sqliteName);
              console.log(`Deleted SQLite database ${sqliteName}`);
            } catch (e) {
              // a SQLite database that survives sign-out is the previous user's data left on the
              // device. That is leak-shaped, so it must reach Sentry, not just the console.
              console.error(`SQLite database ${sqliteName} delete failed`, e);
              failedSqliteNames.push(sqliteName);
              if (firstSqliteError === undefined) firstSqliteError = e;
            }
          }
        }
        if (failedSqliteNames.length > 0) {
          deps.logging.trackSilentProblem('SignOutSqliteDeletionFailed', {
            failed_count: failedSqliteNames.length,
            failed_db_names: failedSqliteNames.join(','),
            ...errorReportFields(firstSqliteError),
          });
        }
        // ONLY drop the cleanup marker once the SQLite databases are really gone, so the next
        // account starts from a clean device rather than re-running a cleanup against leftovers
        // this sign-out could not remove — which is why the marker was carried across the wipe
        // above. Dropping it costs nothing worse than one extra enumeration on the next boot;
        // an older version of this comment said it would import the previous user's SQLite data
        // into IndexedDB, which is not something any code here does. See
        // `design-docs/local-storage-and-auto-plan-cache.md`, "Re-arming the cleanup (#4834)".
        if (failedSqliteNames.length === 0) {
          deps.localStorage.removeItem(SQLITE_MIGRATION_DONE_KEY);
          // The background-copy note goes on exactly the same terms (#5449): drop it ONLY
          // once every file it could name is really gone, so the note never outlives the
          // files and never leaks into the next account. If a delete failed the note is
          // KEPT — which is not untidiness but the retry: a file still on the device stays
          // nameable, so the next sign-out can try again. The Android engine treats a
          // missing file as a successful delete, so a retry of a name whose file has since
          // vanished clears the note rather than making it stick.
          deps.localStorage.removeItem(SQLITE_MIGRATION_TARGETS_KEY);
          // the coach-copy note goes on the same terms, for the same reason (df-a00755b05133)
          deps.localStorage.removeItem(SQLITE_COACH_COPY_NAMES_KEY);
        }
      } catch (e) {
        deps.logging.trackSilentProblem('SignOutSqliteCleanupFailed', errorReportFields(e));
      }
    } else if (deps.localStorage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY) === null) {
      // no SQLite deleter, and the device was never rolled back off SQLite: its coach copies
      // are IndexedDB databases, which the sweep above has already deleted. Drop the note too,
      // so a list of the previous account's client uids does not outlive sign-out.
      // (df-a00755b05133)
      //
      // if the device WAS rolled back (#5270), keep the note. The rollback only removes the
      // SQLite databases it copied back, so a coach copy closed before it can still be a SQLite
      // file, and this note is the only record of its name. A rolled-back device whose note
      // names a copy is given a deleter (df-4576933c7cf2, `signOutMustDeleteSqliteFiles`), so
      // it takes the branch above; this one keeps a note only when it names nothing readable.
      deps.localStorage.removeItem(SQLITE_COACH_COPY_NAMES_KEY);
    }

    // #5692: the native apps keep the user's photos as files, outside every store above. BOUNDED
    // like the other slow steps, so a hung file delete cannot hold the reload past its budget
    if (deps.deleteNativeImageCacheAsync) {
      try {
        await timeout(NATIVE_IMAGE_CACHE_DELETE_TIMEOUT_MS, deps.deleteNativeImageCacheAsync());
      } catch (err) {
        deps.logging.trackSilentProblem('SignOutImageCacheDeletionFailed', errorReportFields(err));
      }
    }

    // clear other caches
    const cacheKeys = await deps.caches.keys();
    for (const cacheKey of cacheKeys) {
      await deps.caches.delete(cacheKey);
    }

    // clear again, LAST, for the reason spelled out at the early clear above: the app has been alive
    // for seconds since then with live sync still running, and its `paused` handler re-creates
    // `db_backgroundSyncComplete_*` the moment it finds the key gone. Anything a late writer put back
    // dies here.
    //
    // Still preserve the cleanup marker, and note the subtlety: preserving means "carry across
    // whatever the SQLite block decided". If those deletes succeeded, the block already removed the
    // marker, so there is nothing to carry and it stays gone. If one FAILED, the block deliberately
    // kept it — and clearing it here would silently undo that. Passing false here would not be
    // "neutral"; it would destroy the one decision this marker exists to record.
    clearSessionStorageAndCookies(deps);
  }
}

/**
 * Run the whole pre-reload teardown and then reload the page — the guarantee the caller depends on.
 *
 * ALWAYS reloads and NEVER rejects. `MyApp.reloadAppAsync` raises `reloadPending` before calling
 * this and nothing ever resets it, so a path that skipped the reload would strand the app for good.
 *
 * **The dependencies arrive as a FUNCTION, and `logging` arrives separately, on purpose.** Building
 * the dependency object can throw: the browser globals it names may be missing or refused, and a
 * throw at the CALL SITE would land outside the try/finally below, where it skips the reload and
 * wedges the app for good. Calling `buildDeps` HERE puts it inside. `logging` is handed over on its
 * own because it is the one thing still needed when `buildDeps` itself is what threw.
 *
 * That keeps the reload guaranteed; it does NOT decide WHERE in the teardown such a throw lands.
 * The caller settles that by handing over the risky globals lazily, so each is read at the step
 * that uses it rather than all of them up front — see `buildReloadTeardownDeps` in
 * `app.component.ts`, which explains why and which `reload-teardown.test.ts` pins.
 */
export async function runReloadTeardownAsync(
  logging: TeardownLogging,
  buildDeps: () => ReloadTeardownDeps,
  opts: ReloadAppOptions,
  reload: () => void,
): Promise<void> {
  // the reload MUST run no matter how the preparation ends, so it lives in a finally — and
  // the preparation is BOUNDED, because a finally does not save us from an await that never
  // settles. Both matter: reloadPending is already true and nothing resets it, so any path that
  // skipped the reload would strand the app for good (nav-controller ignores every navigation
  // while reloadPending, and the L8 navRestoring watchdog deliberately does not fire during a
  // pending reload, so the full-screen spinner would never clear either) — a silent, permanent
  // wedge with zero telemetry. Both failure modes are real: the teardown can reject (firebase
  // shutdown, caches.delete), and it can HANG — the telemetry flush retries a failing
  // server-side flush in an unbounded backoff loop, which is exactly what a degraded server does,
  // so a sign-out during one would otherwise never reload. (database-recovery.service.ts bounds
  // the same flush call for the same reason.) Reloading regardless is right: a reload IS the recovery, and
  // it keeps the invariant the watchdog relies on — reloadPending === true means a reload really
  // is coming.
  try {
    await timeout(RELOAD_PREPARE_TIMEOUT_MS, prepareForReloadAsync(buildDeps(), opts));
  } catch (err) {
    // trackSilentProblem, NOT trackAbnormalException: the latter first routes through
    // interceptDatabaseError, which can swallow the report into the DB-recovery machinery and
    // re-enter the reload — and its userMessage would show an untranslated toast nobody can
    // read, since the page reloads in the same turn.
    logging.trackSilentProblem('ReloadPrepareFailed', {
      ...errorReportFields(err),
      reset_to_root: opts.resetToRoot,
      clear_local_data: opts.clearLocalData ?? false,
    });
  } finally {
    reload();
  }
}


/**
 * The image outbox's local database name. (#4981 phase 3, R13)
 *
 * A literal rather than an import: the outbox lives on an Angular service that
 * reaches `pouchdb-browser`, which does not load under plain node, and this module
 * must stay node-testable. `reload-teardown.test.ts` cross-checks this string
 * against the `OUTBOX_DB_NAME` that service exports, by reading its source text, so
 * the copy cannot drift silently.
 */
const IMAGE_UPLOAD_OUTBOX_DB_NAME = 'image_upload_outbox';

/**
 * The food unpublish outbox's local database name. (df-efc66c4a2f6e)
 *
 * A literal for the same reason as the image outbox above, and cross-checked the
 * same way against the `FOOD_UNPUBLISH_OUTBOX_DB_NAME` that service exports.
 *
 * It holds no food contents — only the ids of shared food copies this device still
 * owes the server a removal for — but it is still the previous user's business, and
 * a queued removal replayed under whoever signs in next would be asked with the wrong
 * account's credentials.
 */
const FOOD_UNPUBLISH_OUTBOX_DB_NAME = 'food_unpublish_outbox';

/**
 * Every local database name whose SQLite file sign-out must delete. (#4981 phase 3, R13)
 *
 * **Why the provider's list alone is not enough.** The teardown's SQLite deleter
 * walks `DatabaseWrapperProvider.allDatabases`, and some of this app's databases
 * never go through that provider: the image upload outbox, the food unpublish
 * outbox, and the coach staging database (`global_exercises`) — each is a raw
 * `new PouchDB(...)` owned by one service. On IndexedDB they were still wiped, because the
 * teardown separately deletes every IndexedDB database on the origin. On SQLite
 * there is no such sweep — the deleter is handed names — so each of those files
 * simply survived sign-out, leaving the previous user's queued photo bytes, their
 * queued food removals and their staged coach exercises on the device for whoever
 * signs in next.
 *
 * That was a known iOS-only bug while iOS was the only SQLite platform. Phase 3 is
 * what puts Android devices on SQLite, so phase 3 is what fixes it — phase 2
 * recorded the consequence and left it here deliberately.
 *
 * **`migrationTargetNames` is the #5449 half, and it is not the same list.** Until
 * #5449 a SQLite target never outlived the boot that made it: the copy ran on the
 * splash, and a failed attempt deleted its target before the next try. A BACKGROUND
 * copy keeps its target across boots deliberately — that is what lets it resume rather
 * than start from zero — so a device still running on IndexedDB can now be carrying
 * SQLite files full of the signed-in user's data. Those files are invisible to
 * everything else: the provider never held them, and phase 4's cleanup cannot see them
 * either. Sign-out is the only thing standing between them and the next person to sign
 * in on that device.
 *
 * They are passed in rather than read here because this module must stay free of browser
 * globals; the caller reads them from `localStorage`. **By the time it does, both wipes
 * have already run** — `clearSessionStorageAndCookies` clears storage early and this block
 * asks for the names late — so the read finds anything at all only because both wipes
 * preserve the key on purpose: the sign-out wipe via `DEVICE_LEVEL_LOCALSTORAGE_KEYS`, and
 * this module's own early clear by snapshotting and restoring it. Assuming otherwise is
 * what left a signed-out person's whole database on a phone in #5453. See
 * design-docs/android-local-database-engine.md, "Some notes must survive BOTH localStorage
 * wipes".
 *
 * **`coachCopyNames` is the df-a00755b05133 half.** A coach who managed a client opened
 * `userdb-coach_<clientUid>`, and "Back to my account" keeps that copy but closes it, so by
 * sign-out the provider no longer holds it. The caller reads the names from the same kind of
 * note, `SQLITE_COACH_COPY_NAMES_KEY`, which both wipes preserve for the same reason.
 *
 * Duplicates are removed because a name could legitimately appear on both sides — a
 * recorded target is normally also a provider database — and deleting the same file
 * twice would report a spurious failure the second time, which reads as leaked user
 * data.
 */
export function sqliteDatabaseNamesForSignOut(
  providerNames: (string | null | undefined)[],
  migrationTargetNames: (string | null | undefined)[] = [],
  coachCopyNames: (string | null | undefined)[] = [],
): string[] {
  const names = new Set<string>();
  for (const name of [...providerNames, ...migrationTargetNames, ...coachCopyNames]) {
    if (name) names.add(name);
  }
  names.add(IMAGE_UPLOAD_OUTBOX_DB_NAME);
  names.add(FOOD_UNPUBLISH_OUTBOX_DB_NAME);
  names.add(exerciseDatabaseName);
  return [...names];
}
